import { cn } from '@/lib/utils/utils';
import { formatRef } from '@/lib/utils/bible-utils';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { nodeAccentColorFromPercent } from '@/lib/utils/color-utils';
import { useMemo } from 'react';
import { buildLeafSpans, computeGroupSeparators, gridTemplateRowsFromSpans } from '@/lib/canon-context/book-rail-utils';
import { motion } from 'framer-motion';

interface BookRailProps {
    outline: Outline;
    currentNode?: OutlineNode;
    highlightedNode?: OutlineNode;
    isDarkMode?: boolean;
    onNodeClick?: (node: OutlineNode) => void;
    focusedNode?: OutlineNode;
    mode?: 'focused' | 'full';
    transitionNodeId?: string;
}

import { isDescendantOf } from '@/lib/outline/outline-tree-utils';

export function BookRail({ outline, currentNode, highlightedNode, isDarkMode, onNodeClick, focusedNode, mode = 'focused', transitionNodeId }: BookRailProps) {
    const { spans } = useMemo(() => buildLeafSpans(outline), [outline]);
    const separators: number[] = useMemo(() => computeGroupSeparators(outline, focusedNode, spans), [outline, focusedNode, spans]);
    const gridTemplateRows = useMemo(() => gridTemplateRowsFromSpans(spans), [spans]);

    return (
        <div
            className={cn(
                'relative w-4 md:w-6 h-full rounded-xl overflow-hidden border',
                isDarkMode ? 'border-white/20 bg-white/10' : 'border-white/20 bg-white/10'
            )}
            aria-label="Book rail"
        >
            <div className="grid w-full h-full" style={{ gridTemplateRows }}>
                {spans.map((span, index) => {
                    const node = span.node;
                    const isChild = highlightedNode ? isDescendantOf(node, highlightedNode) : false;
                    const isUnderCurrent = currentNode ? (isDescendantOf(node, currentNode) && !isChild) : false;
                    const isFirst = index === 0;
                    const isLast = index === spans.length - 1;
                    const midPercent = span.midPercent;
                    return (
                        <Tooltip key={`${node.id}-${index}`} delayDuration={250}>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    className={cn(
                                        "cursor-pointer w-full h-full transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60",
                                        isFirst && "rounded-t-xl",
                                        isLast && "rounded-b-xl"
                                    )}
                                    data-node-id={node.id}
                                    data-rail="book"
                                    style={{
                                        backgroundColor: nodeAccentColorFromPercent(midPercent, !!isDarkMode),
                                        border: isChild ? '2px solid rgba(250,204,21,0.9)' : 'none',
                                        opacity: isUnderCurrent ? 0.6 : isChild ? 1 : 0.8
                                    }}
                                    onClick={() => onNodeClick?.(node)}
                                >
                                    {transitionNodeId === node.id && (
                                        <motion.div
                                            layoutId={`rail-icicle-${node.id}`}
                                            className="absolute inset-0"
                                            style={{ border: '2px solid rgba(250,204,21,0.9)', borderRadius: 8 }}
                                        />
                                    )}
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs">
                                <div className="text-xs">
                                    <div className="font-semibold">{outline.book} {formatRef(node.ref)}</div>
                                    <div className="opacity-80">{node.title}</div>
                                </div>
                            </TooltipContent>
                        </Tooltip>
                    );
                })}
            </div>
            {/* Depth-based separators */}
            {separators.map((percent, idx) => (
                <div key={`sep-${idx}`} className="absolute left-0 right-0 h-[1px] bg-white/20" style={{ top: `${percent}%` }} />
            ))}
        </div>
    );
}


